import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tag, Row, Col, Typography, Alert, Space, Flex, Spin, message } from 'antd';
import { EyeOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import WizardProgress from './WizardProgress';
import WizardFileInfoView from './WizardFileInfoView';
import WizardLoadInfoView from './WizardLoadInfoView';
import LoadFilesTable from '../load/files/LoadFilesTable';
import LoadFileReviewCompact from '../load/files/LoadFileReviewCompact';
import LoadFileReviewError from '../load/files/LoadFileReviewError';
import LoadRouteInfo from '../load/files/LoadRouteInfo';
import { check_file, set_selected_node, get_all_files, confirm_file, discard_file } from '../../redux/actions/selfActions';
import { get_load_status_color } from '../../utils/get_color_status';
import isEmpty from '../../utils/isEmpty';

const WizardStatus = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [messageApi, contextHolder] = message.useMessage();
	const params = useParams();

	// Extract file_id or load_id from URL params
	const urlParam = params['*']; // This captures the dynamic part after /wizard/status/
	let fileIdFromUrl = null;
	let loadIdFromUrl = null;

	if (urlParam) {
		if (urlParam.startsWith('file=')) {
			fileIdFromUrl = urlParam.substring(5);
		} else if (urlParam.startsWith('load=')) {
			loadIdFromUrl = urlParam.substring(5);
		}
	}

	const [loads, setLoads] = useState([]);
	const [allCompleted, setAllCompleted] = useState(false);
	const [openModal, setOpenModal] = useState(false);

	// Get wizard state from localStorage
	const [wizardState, setWizardState] = useState(() => JSON.parse(localStorage.getItem('wizardState') || '{}'));

	// Listen for localStorage changes and reload on mount
	useEffect(() => {
		// Reload wizard state when component mounts
		const currentState = JSON.parse(localStorage.getItem('wizardState') || '{}');
		setWizardState(currentState);

		const handleStorageChange = () => {
			const updatedState = JSON.parse(localStorage.getItem('wizardState') || '{}');
			setWizardState(updatedState);
		};

		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, []);

	// Redux state
	const { file_check_loading, file_check, self_errors, selected_node, file_info_loading, file_info, all_files_loading, all_files } = useSelector(state => state.self);

	useEffect(() => {
		// If viewing a specific file from URL, check that file
		if (fileIdFromUrl) {
			dispatch(check_file(true, fileIdFromUrl));
			return;
		}
		
		// Check if this is an initial load
		const isInitialLoad = wizardState.loadType === 'initial' || 
							  (wizardState.uploads && wizardState.uploads.length > 0 && 
							   wizardState.uploads[0].loadType === 'Inicial');
		
		if (isInitialLoad && wizardState.load_id) {
			// For initial loads, get all files by load_id
			dispatch(get_all_files({
				load: wizardState.load_id
			}));
		} else if (!wizardState.lastFileId) {
			// If we don't have a lastFileId, try to get the most recent file upload
			dispatch(get_all_files({
				page: 1,
				size: 1,
				sort: 'date',
				order: 'desc'
			}));
		} else {
			dispatch(check_file(true, wizardState.lastFileId));
		}
	}, [dispatch, fileIdFromUrl, wizardState.lastFileId, wizardState.load_id, wizardState.loadType, wizardState.uploads]);

	// When we get the files list, use the most recent one
	useEffect(() => {
		if (!wizardState.lastFileId && all_files && all_files.length > 0) {
			const mostRecentFile = all_files[0];
			dispatch(check_file(true, mostRecentFile._id.$oid));
		}
	}, [all_files, wizardState.lastFileId, dispatch]);

	useEffect(() => {
		if (!isEmpty(selected_node)) {
			setOpenModal(true);
		}
	}, [selected_node]);

	useEffect(() => {
		const interval = setInterval(() => {
			const fileId = wizardState.lastFileId || (all_files && all_files.length > 0 ? all_files[0]._id.$oid : null);
			if (self_errors.hasOwnProperty('check_file') && fileId) {
				dispatch(check_file(false, fileId));
			}
			else {
				clearInterval(interval);
			}
		}, 1000 * 10);

		// Clean interval
		return () => clearInterval(interval);
	}, [self_errors, dispatch, wizardState.lastFileId, all_files]);

	useEffect(() => {
		// Check if all loads are completed
		const completed = loads.every(load =>
			load.status === 'Exitoso' || load.status === 'Completado'
		);
		setAllCompleted(completed && loads.length > 0);
	}, [loads]);

	const handleFinish = () => {
		if (wizardState.cycleSelectionType === 'new') {
			// Navigate to load page 
			navigate(`/load/${wizardState.load_id}/files`);
		}
		else {
			// Navigate to main dashboard or loads page
			navigate('/load-status');
		}

		// Clear wizard state
		localStorage.removeItem('wizardState');
	};

	const onCancel = () => {
		setOpenModal(false);
		dispatch(set_selected_node({}));
	};

	const handleViewDetails = (record) => {
		// Navigate to load details
		navigate(`/load/${record.key}`);
	};

	const handleRemoveLoad = (record) => {
		setLoads(loads.filter(load => load.key !== record.key));
	};

	const handleMarkAsCompleted = (record) => {
		setLoads(loads.map(load =>
			load.key === record.key
				? { ...load, status: 'Exitoso' }
				: load
		));
	};

	// Functions for file management in initial loads
	const confirmFile = (record) => {
		dispatch(confirm_file(messageApi, record._id.$oid));
	};

	const discardFile = (record) => {
		const filters = { load: wizardState.load_id };
		dispatch(discard_file(messageApi, record._id.$oid, filters));
	};

	const columns = [
		{
			title: 'NOMBRE',
			dataIndex: 'name',
			key: 'name'
		},
		{
			title: 'DESCRIPCIÓN',
			dataIndex: 'description',
			key: 'description'
		},
		{
			title: 'TIPO DE CARGA',
			dataIndex: 'loadType',
			key: 'loadType'
		},
		{
			title: 'FUENTE',
			dataIndex: 'source',
			key: 'source'
		},
		{
			title: 'ESTADO',
			dataIndex: 'status',
			key: 'status',
			render: (status) => (
				<Tag color={get_load_status_color(status === 'Creado' ? 1 : status === 'Exitoso' ? 5 : 2)}>
					{status}
				</Tag>
			)
		},
		{
			title: 'FECHA',
			dataIndex: 'date',
			key: 'date'
		},
		{
			title: '',
			key: 'actions',
			width: 150,
			render: (_, record) => (
				<Space>
					<Button
						type="primary"
						shape="circle"
						size="small"
						icon={<EyeOutlined />}
						onClick={() => handleViewDetails(record)}
						title="Ver detalles"
					/>
					<Button
						danger
						shape="circle"
						size="small"
						icon={<CloseOutlined />}
						onClick={() => handleRemoveLoad(record)}
						title="Remover"
						style={{ 
							backgroundColor: '#ff4d4f',
							borderColor: '#ff4d4f',
							color: 'white'
						}}
					/>
					{record.status !== 'Exitoso' && (
						<Button
							type="default"
							shape="circle"
							size="small"
							icon={<CheckOutlined />}
							onClick={() => handleMarkAsCompleted(record)}
							title="Marcar como exitoso"
							style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
						/>
					)}
				</Space>
			)
		}
	];

	// For wizard flow, we need to get the file_id from the most recent upload
	// Since we don't have direct file_id, we'll simulate the LoadFileInfo structure
	const fileId = wizardState.uploads?.[0]?.file_id || null;

	return (
		<div className="wizard-container">
			{contextHolder}
			<WizardProgress currentStep={4} />

			<Row justify='center' style={{ padding: '40px 0' }}>
				<Col span={24}>
					<LoadRouteInfo openModal={openModal} node={selected_node} onCancel={onCancel} />

					<Typography.Title className='text-center' level={2}>
						{(() => {
							const isInitialLoad = wizardState.loadType === 'initial' || 
												  (wizardState.uploads && wizardState.uploads.length > 0 && 
												   wizardState.uploads[0].loadType === 'Inicial');
							
							if (isInitialLoad) {
								return 'Información de la Carga';
							} else {
								return t('loadStatusDetails.title') || 'Información del Archivo';
							}
						})()}
					</Typography.Title>

					{/* Show component based on URL parameters */}
					{(() => {
						// If load_id is in URL, show load info
						if (loadIdFromUrl) {
							return <WizardLoadInfoView key={`load-${loadIdFromUrl}`} messageApi={messageApi} loadId={loadIdFromUrl} />;
						}
						// If file_id is in URL, show file info
						else if (fileIdFromUrl) {
							return <WizardFileInfoView key={`file-${fileIdFromUrl}`} messageApi={messageApi} fileId={fileIdFromUrl} isInitialLoad={false} />;
						}
						// Fallback to original logic
						else {
							const isInitialLoad = wizardState.loadType === 'initial' || 
												  (wizardState.uploads && wizardState.uploads.length > 0 && 
												   wizardState.uploads[0].loadType === 'Inicial');
							
							if (isInitialLoad && wizardState.load_id) {
								return <WizardLoadInfoView key={`load-${wizardState.load_id}`} messageApi={messageApi} loadId={wizardState.load_id} />;
							} else {
								const fileId = wizardState.lastFileId || (all_files && all_files.length > 0 ? all_files[0]._id.$oid : null);
								return fileId ? <WizardFileInfoView key={`file-${fileId}`} messageApi={messageApi} fileId={fileId} isInitialLoad={isInitialLoad} /> : null;
							}
						}
					})()}

					{/* For initial loads, show files table only when viewing load (not specific file) */}
					{(() => {
						// Don't show files table if we're viewing a specific file
						if (fileIdFromUrl) {
							return null;
						}
						
						const isInitialLoad = wizardState.loadType === 'initial' || 
											  (wizardState.uploads && wizardState.uploads.length > 0 && 
											   wizardState.uploads[0].loadType === 'Inicial');
						
						if (isInitialLoad && wizardState.load_id) {
							return (
								<div style={{ marginTop: '30px' }}>
									<Row justify='center'>
										<Col span={24} style={{ maxWidth: 1000 }}>
											<Typography.Title level={3} style={{ textAlign: 'center', marginBottom: '20px' }}>
												Archivos de la Carga
											</Typography.Title>
											<LoadFilesTable 
												onConfirm={confirmFile}
												onDiscard={discardFile}
												loadId={wizardState.load_id}
												isWizardContext={true}
											/>
										</Col>
									</Row>
								</div>
							);
						}
						return null;
					})()}

					{/* Show file check section when viewing specific file or for non-initial loads */}
					{(() => {
						// Always show file check section if viewing a specific file
						if (fileIdFromUrl) {
							return (
								<>
									{file_check_loading
										? <Flex justify='center' style={{ marginTop: '1.5rem' }}>
											<Spin size='large' />
										</Flex>
										: self_errors.hasOwnProperty('check_file')
											? <Row justify='center'>
												<Alert
													style={{ marginTop: '1.5rem', width: 1000 }}
													message={t('loadStatusDetails.check.alert.msg')}
													description={t('loadStatusDetails.check.alert.desc')}
													type='info'
													showIcon
												/>
											</Row>
											: !isEmpty(file_check)
												? <>
													{file_check.data !== null && file_check.error === null &&
														// Check if hierarchy is present, if so, use new function, if not, render the old component
														file_check?.file?.hierarchy
														? <LoadFileReviewCompact
															treeData={file_check?.data}
														/>
														: <LoadFileReviewCompact
															treeData={file_check?.data?.regions}
														/>
													}
													{file_check.data === null && file_check.error !== null &&
														<LoadFileReviewError
															error={file_check.error}
														/>
													}
												</>
												: null
									}
								</>
							);
						}
						
						const isInitialLoad = wizardState.loadType === 'initial' || 
											  (wizardState.uploads && wizardState.uploads.length > 0 && 
											   wizardState.uploads[0].loadType === 'Inicial');
						
						if (isInitialLoad) {
							return null; // Don't show file check section for initial loads without specific file
						}
						
						return (
							<>
								{file_check_loading
									? <Flex justify='center' style={{ marginTop: '1.5rem' }}>
										<Spin size='large' />
									</Flex>
									: self_errors.hasOwnProperty('check_file')
										? <Row justify='center'>
											<Alert
												style={{ marginTop: '1.5rem', width: 1000 }}
												message={t('loadStatusDetails.check.alert.msg')}
												description={t('loadStatusDetails.check.alert.desc')}
												type='info'
												showIcon
											/>
										</Row>
										: !isEmpty(file_check)
											? <>
												{file_check.data !== null && file_check.error === null &&
													// Check if hierarchy is present, if so, use new function, if not, render the old component
													file_check?.file?.hierarchy
													? <LoadFileReviewCompact
														treeData={file_check?.data}
													/>
													: <LoadFileReviewCompact
														treeData={file_check?.data?.regions}
													/>
												}
												{file_check.data === null && file_check.error !== null &&
													<LoadFileReviewError
														error={file_check.error}
													/>
												}
											</>
											: null
								}
							</>
						);
					})()}

					{/* Wizard specific finish button */}
					<Row justify="center" style={{ marginTop: '40px' }}>
						<Col>
							<Button
								type="primary"
								size="large"
								onClick={handleFinish}
								style={{
									height: '60px',
									fontSize: '18px',
									fontWeight: '600',
									minWidth: '200px',
									borderRadius: '8px'
								}}
							>
								TERMINAR
							</Button>
						</Col>
					</Row>
				</Col>
			</Row>
		</div>
	);
};

export default WizardStatus;